/**
 * RingCenter — the authed unified comms workspace shell (`/ring-center`,
 * and the default at `/phone`). Faithful to the approved design preview:
 * slim royal-blue top bar (with search / presence / New / bell / avatar),
 * a 3-pane control room (RingWorkspace list+thread → ClientIntelPanel),
 * a floating softphone over the conversation, plus Chat / Meet / Blast /
 * Contacts / Fax sections. ADDITIVE — the old pages stay one click away.
 */
import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Inbox, Phone, MessageSquare, Megaphone, Video, Users, Printer,
  Search, Plus, Bell, Leaf, ChevronDown, Check, Activity, Loader2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { twilioAPI } from "../services/api";
import { useCall } from "../components/layout/ActiveCallOverlay";
import RingWorkspace from "./ring/RingWorkspace";
import TeamOnCallStrip from "../components/comms/TeamOnCallStrip";
import ClientIntelPanel from "./ring/ClientIntelPanel";
import RingFloatingSoftphone from "./ring/RingFloatingSoftphone";
import Communications2 from "./Communications2";
import NewTextComposerModal from "./comms/NewTextComposerModal";
import { last10 } from "./comms/useInboxRollup";

const LiveWebChat = lazy(() => import("./ring/RingLiveChat"));
const MeetPage = lazy(() => import("./Video"));

const NAVY_DEEP = "#002244";
const GOLD = "#FFDA00";
const GREEN = "#1D9E75";
const RED = "#C0392B";
const ROYAL_BLUE = "#1668DA";
const ROYAL = "#2A7CC4";
const MUTED = "#7B8FA3";
const HAIRLINE = "rgba(3,40,102,0.10)";
const NAVY_TINT = "rgba(3,40,102,0.06)";
const FONT_BODY = "'IBM Plex Sans', sans-serif";

// Display labels tuned to the approved mockup (ids/routing unchanged).
const NAV_LABELS = { phone: "Dialer", blast: "Text blasts", chat: "Live chat" };

// section → behaviour. `tab` is what Communications2 reads (blast/contacts/
// fax) or the workspace sub-view; `mode` decides which centre pane mounts.
const SECTIONS = [
  { id: "inbox", label: "Inbox", icon: Inbox, mode: "workspace", sub: "All" },
  { id: "phone", label: "Phone", icon: Phone, mode: "workspace", sub: "All", phone: true },
  { id: "text", label: "Text", icon: MessageSquare, mode: "workspace", sub: "Texts" },
  { id: "blast", label: "Blast", icon: Megaphone, mode: "comms", tab: "text-blast" },
  { id: "chat", label: "Chat", icon: MessageSquare, mode: "chat" },
  { id: "meet", label: "Meet", icon: Video, mode: "meet" },
  { id: "contacts", label: "Contacts", icon: Users, mode: "comms", tab: "contacts" },
  { id: "fax", label: "Fax", icon: Printer, mode: "comms", tab: "fax" },
];

export default function RingCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { activeCall, deviceReady, initializing, initializeDevice } = useCall();
  const [params, setParams] = useSearchParams();

  const section = params.get("section") || "inbox";
  const current = SECTIONS.find((s) => s.id === section) || SECTIONS[0];
  const openKey = params.get("who") || null;

  const [sub, setSub] = useState(current.sub || "All");
  const [callOpen, setCallOpen] = useState(false);
  const [newTextOpen, setNewTextOpen] = useState(false);
  const [compose, setCompose] = useState(null);
  const [ctxOpen, setCtxOpen] = useState(() => localStorage.getItem("rc_ctx_open") !== "0");

  const setOpenKey = useCallback((key) => {
    setParams((p) => { const n = new URLSearchParams(p); if (key) n.set("who", key); else n.delete("who"); return n; }, { replace: true });
  }, [setParams]);

  const setSection = (id) => setParams((p) => { const n = new URLSearchParams(p); n.set("section", id); return n; }, { replace: true });

  // Start a brand-new text conversation (recipient not yet in the inbox).
  const startNewText = useCallback((tenDigits, contact) => {
    const d = last10(tenDigits);
    if (!d) return;
    const phone = `+1${d}`;
    const name = contact?.name || contact?.display_name || contact?.full_name || "";
    setCompose({ phone, name });
    setNewTextOpen(false);
    setSub("Texts");
    setParams((p) => { const n = new URLSearchParams(p); n.set("section", "text"); n.set("who", phone); return n; }, { replace: true });
  }, [setParams]);

  const onSelect = (s) => {
    if (s.phone) { setCallOpen(true); return; }
    if (s.sub) setSub(s.sub);
    setParams((p) => {
      const n = new URLSearchParams(p);
      n.set("section", s.id);
      if (s.tab) n.set("tab", s.tab);
      return n;
    }, { replace: true });
  };
  const isActive = (s) => (s.phone ? callOpen : s.id === section);

  const toggleCtx = () => setCtxOpen((v) => { localStorage.setItem("rc_ctx_open", v ? "0" : "1"); return !v; });

  // Auto-open the softphone the moment a call goes active (inbound answered OR
  // outbound placed) so the in-call controls — Transfer, Park, Department,
  // Hold, Keypad — are ALWAYS visible without hunting for the dialer FAB.
  const prevCallKey = useRef(null);
  useEffect(() => {
    const key = activeCall ? (activeCall.sid || activeCall.id || activeCall.number || "active") : null;
    if (key && key !== prevCallKey.current) setCallOpen(true);
    prevCallKey.current = key;
  }, [activeCall]);

  // active phone for the context panel (from the selected rollup key)
  const activePhone = (() => {
    if (!openKey) return "";
    const d = last10(String(openKey).split("::")[0]);
    return d.length === 10 ? `+1${d}` : d;
  })();

  const mode = current.mode;
  const showContext = mode === "workspace";

  // ── unread badges (Inbox needs-response + Text unread via Communications2
  // rollup, Chat via poll). We mount a hidden Communications2 counter only
  // when not already showing a comms surface, to avoid double fetch.
  const [badges, setBadges] = useState({ inbox: 0, text: 0, chat: 0 });
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/im/unread-count`, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
        if (r.ok) { const d = await r.json(); if (!cancelled) setBadges((b) => ({ ...b, chat: d?.count || 0 })); }
      } catch { /* keep last */ }
      try {
        const r2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/unread-counts`, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
        if (r2.ok) { const d2 = await r2.json(); if (!cancelled) setBadges((b) => ({ ...b, text: d2?.total || 0 })); }
      } catch { /* keep last */ }
    };
    load();
    // EV-06: poll badges at a calmer cadence and pause while the tab is
    // hidden (no point hammering the SMS/chat unread endpoints in the
    // background). Fire once immediately on refocus so badges feel live.
    const id = setInterval(() => { if (!document.hidden) load(); }, 60000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);
  const badgeFor = (id) => (id === "text" ? badges.text : id === "chat" ? badges.chat : 0);

  // ── phone-line switcher: the real lines this user can call from
  // (GET /api/twilio/available-numbers), replacing the old hardcoded pill.
  const [lines, setLines] = useState([]);
  const [activeLine, setActiveLine] = useState(() => localStorage.getItem("rc_active_line") || "");
  const [lineMenuOpen, setLineMenuOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await twilioAPI.getAvailableNumbers();
        const list = (r.data || []).filter((n) => n && n.phone_number);
        // Avida-named lines first so the org's own line is the default.
        list.sort((a, b) =>
          ((a.company_name || "").toLowerCase().includes("avida") ? 0 : 1) -
          ((b.company_name || "").toLowerCase().includes("avida") ? 0 : 1)
        );
        if (cancelled) return;
        setLines(list);
        setActiveLine((prev) =>
          prev && list.some((l) => l.phone_number === prev) ? prev : (list[0]?.phone_number || "")
        );
      } catch { /* honest empty state */ }
    })();
    return () => { cancelled = true; };
  }, []);
  useEffect(() => { if (activeLine) localStorage.setItem("rc_active_line", activeLine); }, [activeLine]);

  const fmtPhone = (p) => {
    const d = String(p || "").replace(/\D/g, "").slice(-10);
    return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || "");
  };
  const lineLabel = (l) => l?.company_name || (l?.type === "personal" ? "My line" : "Line");
  const lineInitials = (l) => ((lineLabel(l).match(/\b\w/g) || []).slice(0, 2).join("").toUpperCase() || "LN");
  const activeLineObj = lines.find((l) => l.phone_number === activeLine) || null;

  const initials = ((user?.name || user?.email || "?").match(/\b\w/g) || []).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="ring-center" className="-mx-4 -my-4 md:-mx-8 md:-my-8 flex flex-col overflow-hidden bg-white" style={{ height: "100vh", fontFamily: FONT_BODY }}>
      {/* ── TOP BAR (clean white, per approved design) ───────── */}
      <div data-testid="rc-topbar" className="shrink-0 flex items-center gap-3 px-4" style={{ height: 56, background: "#fff", borderBottom: `1px solid ${HAIRLINE}` }}>
        {/* Brand */}
        <div className="flex items-center gap-2.5 shrink-0 pr-2">
          <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: NAVY_DEEP }}>
            <Leaf size={18} color={GOLD} strokeWidth={2.2} />
          </div>
          <div className="hidden md:block leading-tight">
            <div className="text-[15px] font-extrabold" style={{ color: NAVY_DEEP }}>Ring Center</div>
            <div className="text-[9px] font-bold tracking-widest" style={{ color: MUTED }}>AVIDA PERSONAL CARE</div>
          </div>
        </div>

        <nav className="flex items-center h-full overflow-x-auto mx-auto">
          {SECTIONS.map((s) => {
            const active = isActive(s);
            const Icon = s.icon;
            const badge = badgeFor(s.id);
            const label = NAV_LABELS[s.id] || s.label;
            return (
              <button key={s.id} data-testid={`rc-nav-${s.id}`} onClick={() => onSelect(s)}
                className="relative h-full flex items-center gap-2 px-3.5 cursor-pointer text-[13.5px] font-semibold shrink-0 transition-colors"
                style={{ color: active ? NAVY_DEEP : MUTED }}>
                <Icon size={18} strokeWidth={2.2} color={active ? NAVY_DEEP : MUTED} />
                {label}
                {badge > 0 && (
                  <span data-testid={`rc-badge-${s.id}`} className="min-w-[18px] h-[18px] px-1 rounded-full grid place-items-center text-[10px] font-bold leading-none" style={{ background: NAVY_DEEP, color: "#fff" }}>{badge > 99 ? "99+" : badge}</span>
                )}
                {active && <span style={{ background: GOLD, height: 3 }} className="absolute left-3 right-3 bottom-0 rounded-full" />}
              </button>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2.5 shrink-0">
          <button data-testid="rc-new" onClick={() => setCallOpen(true)} className="flex items-center gap-1.5 rounded-lg h-9 px-3.5 text-[12.5px] font-bold hover:brightness-95" style={{ background: GOLD, color: NAVY_DEEP }}><Phone size={15} /> New call</button>
          <button data-testid="rc-new-text" onClick={() => setNewTextOpen(true)} className="flex items-center gap-1.5 rounded-lg h-9 px-3.5 text-[12.5px] font-bold hover:brightness-95" style={{ background: NAVY_DEEP, color: "#fff" }}><MessageSquare size={15} /> New text</button>
          <div className="relative block">
            <button
              data-testid="rc-line-switcher"
              onClick={() => lines.length > 1 && setLineMenuOpen((o) => !o)}
              className="flex items-center gap-2 rounded-lg pl-1 pr-1.5 sm:pr-2.5 h-9"
              style={{ border: `1px solid ${HAIRLINE}`, cursor: lines.length > 1 ? "pointer" : "default" }}
              title={lines.length > 1 ? "Switch phone line" : (activeLineObj ? "Active line" : "No line assigned")}
            >
              <div className="w-7 h-7 rounded-md grid place-items-center text-[10px] font-bold shrink-0" style={{ background: NAVY_TINT, color: NAVY_DEEP }}>
                {activeLineObj ? lineInitials(activeLineObj) : "—"}
              </div>
              <div className="leading-tight text-left hidden sm:block">
                <div data-testid="rc-active-line-name" className="text-[11px] font-bold" style={{ color: NAVY_DEEP }}>
                  {activeLineObj ? lineLabel(activeLineObj) : "No line assigned"}
                </div>
                <div className="text-[10px]" style={{ color: MUTED }}>
                  {activeLineObj ? fmtPhone(activeLineObj.phone_number) : "Ask your admin to assign one"}
                </div>
              </div>
              {lines.length > 1 && (
                <ChevronDown size={14} color={MUTED} style={{ transform: lineMenuOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
              )}
            </button>
            {lineMenuOpen && (
              <>
                <button className="fixed inset-0 z-[45] cursor-default" onClick={() => setLineMenuOpen(false)} aria-label="Close line switcher" data-testid="rc-line-backdrop" />
                <div data-testid="rc-line-menu" className="absolute right-0 mt-1 w-64 rounded-xl bg-white shadow-2xl z-[46] py-1.5" style={{ border: `1px solid ${HAIRLINE}` }}>
                  <div className="px-3 py-1.5 text-[10px] font-bold tracking-wider" style={{ color: MUTED }}>SWITCH PHONE LINE</div>
                  {lines.map((l) => {
                    const active = l.phone_number === activeLine;
                    return (
                      <button
                        key={l.phone_number}
                        data-testid={`rc-line-option-${last10(l.phone_number)}`}
                        onClick={() => { setActiveLine(l.phone_number); setLineMenuOpen(false); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="w-7 h-7 rounded-md grid place-items-center text-[10px] font-bold shrink-0" style={{ background: NAVY_TINT, color: NAVY_DEEP }}>{lineInitials(l)}</div>
                        <div className="leading-tight min-w-0 flex-1">
                          <div className="text-[12px] font-bold truncate" style={{ color: NAVY_DEEP }}>{lineLabel(l)}</div>
                          <div className="text-[10px]" style={{ color: MUTED }}>{fmtPhone(l.phone_number)}</div>
                        </div>
                        {active && <Check size={15} color={GREEN} />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => navigate("/call-status")}
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors"
            style={{ background: NAVY_TINT, color: NAVY_DEEP }}
            title="Live call status board"
            data-testid="rc-call-board-btn"
          >
            <Activity size={14} /> Call Board
          </button>
          {/* Permanent phone-connection control — replaces the old floating
              bottom-right "Connect phone" banner. Always lives here so the
              agent knows at a glance whether their phone is ready. */}
          {deviceReady ? (
            <div
              data-testid="rc-phone-status-ready"
              className="flex items-center gap-1.5 px-2.5 h-9 rounded-lg text-[11px] font-bold shrink-0"
              style={{ background: "rgba(29,158,117,0.12)", color: GREEN }}
              title="Your phone is connected and ready for calls"
            >
              <span style={{ background: GREEN }} className="w-2 h-2 rounded-full" />
              <span className="hidden md:inline">Phone ready</span>
            </div>
          ) : (
            <button
              data-testid="rc-connect-phone-btn"
              onClick={() => { if (!initializing) initializeDevice({ isUserGesture: true }); }}
              disabled={initializing}
              className="flex items-center gap-1.5 rounded-lg h-9 px-3 text-[11.5px] font-bold transition-colors disabled:opacity-70 shrink-0"
              style={{ background: initializing ? NAVY_TINT : GOLD, color: NAVY_DEEP }}
              title="Connect your phone to place and receive calls"
            >
              {initializing ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
              {initializing ? "Connecting…" : "Connect phone"}
            </button>
          )}
          <div className="relative hidden sm:block"><Bell size={19} color={NAVY_DEEP} strokeWidth={1.9} />{badges.chat > 0 && <span style={{ background: RED }} className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-white text-[8px] font-bold grid place-items-center">{badges.chat}</span>}</div>
          <div className="flex items-center gap-2 pl-1">
            <div className="w-9 h-9 rounded-full grid place-items-center text-[12px] font-bold" style={{ background: NAVY_DEEP, color: "#fff" }}>{initials}</div>
            <div className="hidden md:block leading-tight">
              <div className="text-[12px] font-bold" style={{ color: NAVY_DEEP }}>{user?.name || "Me"}</div>
              <div className="text-[10px] flex items-center gap-1" style={{ color: GREEN }}><span style={{ background: GREEN }} className="w-1.5 h-1.5 rounded-full" /> Available</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Live "who's on a call" dispatch strip (appears when relevant) ── */}
      <div className="shrink-0 px-4 pt-3 empty:hidden"><TeamOnCallStrip hideWhenEmpty /></div>

      {/* ── BODY ─────────────────────────────────────────────── */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">
        {mode === "workspace" && (
          <RingWorkspace sub={sub} onSub={setSub} openKey={openKey} setOpenKey={setOpenKey} compose={compose} ctxOpen={ctxOpen} setCtxOpen={toggleCtx} onNewText={() => setNewTextOpen(true)} onNewCall={() => setCallOpen(true)} />
        )}
        {mode === "comms" && (
          <div className="flex-1 min-w-0"><Communications2 embedded key={current.tab} /></div>
        )}
        {mode === "chat" && (
          <div className="flex-1 min-w-0"><Suspense fallback={<Loading label="Live Chat" />}><LiveWebChat /></Suspense></div>
        )}
        {mode === "meet" && (
          <div className="flex-1 min-w-0"><Suspense fallback={<Loading label="Team Meetings" />}><MeetPage embedded /></Suspense></div>
        )}

        {showContext && ctxOpen && (
          <ClientIntelPanel
            phone={activePhone}
            onCall={(p) => { setCallOpen(true); }}
            onText={() => { setSection("text"); setSub("Texts"); }}
          />
        )}
      </div>

      {/* Floating softphone — self-positions fixed (defaults top-right, draggable) */}
      {callOpen && (
        <RingFloatingSoftphone onClose={() => setCallOpen(false)} initialTo={activePhone} fromNumber={activeLine} />
      )}

      {/* Green FAB to summon the dialer (above Ask Avi bottom-right) */}
      {!callOpen && (
        <button data-testid="rc-softphone-fab" onClick={() => setCallOpen(true)} className="fixed bottom-28 right-6 w-14 h-14 rounded-full grid place-items-center text-white shadow-2xl z-[60]" style={{ background: activeCall ? GREEN : ROYAL }} title="Open dialer">
          <Phone size={22} />
        </button>
      )}

      <NewTextComposerModal open={newTextOpen} onClose={() => setNewTextOpen(false)} onPick={startNewText} />
    </div>
  );
}

function Loading({ label }) {
  return <div className="h-full grid place-items-center text-slate-400 text-sm">Loading {label}…</div>;
}
